import { Autocomplete, Checkbox, Chip, styled, TextField, useTheme } from '@mui/material';
import { useCallback, useState } from 'react';
import { DynamicIcon } from 'src/components';

interface BaseOptionType {
  id?: string;
  label: string;
  value: unknown;
}

type ConditionalArray<T, Multiple extends boolean | undefined> = Multiple extends true
  ? T[] | null
  : T | null;

interface InlineAutoCompleteProps<T, Multiple extends boolean | undefined> {
  options: T[];
  // these generic "Multiple" typings allow TS to dynamically understand
  // required value type based on multiple choice e.g. value or value[]
  value: (Multiple extends true ? T[] : T) | null;
  onChange: (newValue: ConditionalArray<T, Multiple>) => void;
  icon?: string;
  multiple?: Multiple;
  emptySelectionLabel?: string;
  chipsToDisplay?: number;
  disabled?: boolean;
  sx?: React.CSSProperties;
}

const StyledTextField = styled(TextField, {
  name: 'StyledTextField',
})(({ theme }) => {
  return {
    '.MuiAutocomplete-inputRoot': {
      paddingRight: '0 !important',
      fontSize: '0.8rem',
      flexWrap: 'nowrap',

      '.MuiAutocomplete-input': {
        padding: 0,
        width: 0,
        cursor: 'pointer',
        color: 'inherit',
        minWidth: theme.utils.important('3.1rem'),
        '&:focus': {
          outline: 'none',
        },
        '&::selection': {
          userSelect: 'none',
        },
      },
    },
  };
});

const TagsContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

const AdornmentContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  overflow: 'auto',
  flexWrap: 'nowrap',
  width: 'auto',
  minHeight: '2rem',
  minWidth: '2rem',
  cursor: 'pointer',
});

export const InlineAutoCompleteItem = <
  T extends BaseOptionType,
  Multiple extends boolean | undefined = false,
>({
  options,
  value,
  onChange,
  emptySelectionLabel = 'Select filter',
  multiple,
  icon = 'arrow_drop_down',
  chipsToDisplay = 1,
  disabled,
  sx,
}: InlineAutoCompleteProps<T, Multiple>) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const handleChange = useCallback(
    (_: unknown, option: T | T[] | null) => {
      onChange(option as ConditionalArray<T, Multiple>);
    },
    [onChange],
  );

  const handleIconClick = useCallback(() => {
    if (disabled) {
      return;
    }

    setOpen((prevOpen) => !prevOpen);
  }, [disabled]);

  return (
    <Autocomplete
      disabled={disabled}
      multiple={multiple}
      disableClearable
      disableCloseOnSelect={multiple}
      limitTags={1}
      options={options}
      value={value ?? undefined}
      onChange={handleChange}
      open={open}
      sx={sx}
      isOptionEqualToValue={(option, value) => option.value === value?.value}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      slotProps={{
        paper: {
          sx: {
            ...(theme.palette.mode === 'dark'
              ? {
                  background: theme.palette.custom.OV.sys.surfaceContainerHigh,
                  border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
                  borderRadius: theme.shape.borderRadius / 8,
                }
              : {}),
            '.MuiAutocomplete-option': {
              padding: theme.utils.important(`${multiple ? 0 : '0.4rem 0.7rem'}`),
            },
            '.MuiAutocomplete-option[aria-selected="true"]:hover': {
              background: theme.utils.important(theme.palette.custom.OV.sys.surfaceContainer),
            },
            '.MuiAutocomplete-option[aria-selected="true"]': {
              background: 'transparent',
            },
          },
        },
      }}
      renderOption={({ key, ...props }: { key: string }, option, { selected }) => (
        <li key={key} {...props}>
          {!!multiple && <Checkbox size="small" checked={selected} />}
          {option.label}
        </li>
      )}
      renderTags={(tags, getTagProps) => (
        <TagsContainer>
          {tags.slice(0, chipsToDisplay).map((option, index) => (
            <Chip label={option.label} {...getTagProps({ index })} key={option.id} />
          ))}
          {tags.length > chipsToDisplay && `+${tags.length - chipsToDisplay}`}
        </TagsContainer>
      )}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          variant="standard"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <AdornmentContainer onClick={handleIconClick} aria-label="select">
                <DynamicIcon name={icon} />
                {!!multiple && params.InputProps.startAdornment}
                {!!multiple && !params.InputProps.startAdornment && emptySelectionLabel}
              </AdornmentContainer>
            ),
            endAdornment: null,
            disableUnderline: true,
          }}
        />
      )}
    />
  );
};
